// @flow

import Bugsnag from '@bugsnag/js'
import { useFormik } from 'formik'
import { navigate } from 'gatsby'
import * as React from 'react'

import { useAuth, useSession } from '@toggl/auth'
import type { PricingPlan } from '@toggl/auth/src/types'

import { validate as validatePassword } from '../PasswordRulesTooltip'

const DEFAULT_TIMEZONE = 'Etc/UTC'
declare var process: WebProcess

const validate = (values) => {
  const errors = {}

  if (!values.email) {
    errors.email = 'Required'
  } else if (!/\S+@\S+\.\S+/.test(values.email)) {
    errors.email = 'Invalid email address'
  }

  if (!values.country) {
    errors.country = 'Required'
  }

  if (!values.password) {
    errors.password = 'Required'
  }

  if (values.password) {
    const invalidMessage = validatePassword(values.password)

    if (invalidMessage) {
      errors.password = invalidMessage
    }
  }

  return errors
}

export const useSignupForm = ({
  planId,
  initialError,
}: {
  planId: PricingPlan,
  initialError?: string,
}) => {
  // If no expandable prop then the form is open by default
  const [signupOpen, setSignupOpen] = React.useState(false)
  const [ready, auth] = useAuth()
  const [, session] = useSession()

  const formik = useFormik<any>({
    initialErrors: {
      form: initialError,
    },
    initialValues: {
      email: '',
      password: '',
      tos_accepted: true,
      created_with: 'Firefly',
      country: '',
      timezone: '',
      workspace: { initial_pricing_plan: planId },
    },
    validate,
    validateOnChange: false,
    onSubmit: async ({ country, ...rest }) => {
      const values = {
        workspace: {
          initial_pricing_plan: 0,
        },
        ...rest,
        country_id: parseInt(country),
      }
      // `...rest` is still overwriting workspace, so I'll add this here until
      // we figure it our properly
      if (
        !values.workspace ||
        values.workspace.initial_pricing_plan === undefined
      ) {
        values.workspace = { initial_pricing_plan: 0 }
      }

      try {
        const response = await auth.signup(values)
        if (response.id) {
          session.create(response.id)

          window.dataLayer?.push({
            event: 'signup.completed',
            user_id: response.id,
            source_context: 'non-invite',
            eventCallback: () => navigate('/track/completed-signup/'),
          })

          setTimeout(() => navigate('/track/completed-signup/'), 1200)
        } else if (response.has_account) {
          window.location.assign(process.env.GATSBY_TRACK_APP_URL)
        }
      } catch (e) {
        if (e.meta?.status === 400) {
          formik.setFieldError('form', e.message)
        } else {
          formik.setFieldError(
            'form',
            "We couldn't create your account right now. Please try again in a few minutes."
          )
          Bugsnag.notify(e, (event) => {
            event.addMetadata('xhr', { ...e.meta })
          })
        }
      }
    },
  })

  const toggleSignup = React.useCallback(() => {
    setSignupOpen((isOpen) => !isOpen)
  }, [setSignupOpen])

  React.useEffect(() => {
    if (!ready || formik.values.timezone) {
      return
    }

    const supposedTz = Intl.DateTimeFormat().resolvedOptions().timeZone
    auth
      .timezones()
      .then((timezones) => {
        formik.setFieldValue(
          'timezone',
          timezones.includes(supposedTz) ? supposedTz : DEFAULT_TIMEZONE
        )

        return
      })
      .catch((e) => formik.setFieldError('form', e.message))
  }, [auth, formik, ready])

  React.useEffect(() => {
    if (!ready || formik.values.country) {
      return
    }

    Promise.all([auth.countries(), auth.location()])
      .then(([countries, location]) => {
        const { id } =
          countries.find((c) => c.country_code === location.country_code) || {}

        if (id) {
          formik.setFieldValue('country', id)
        }

        return
      })
      .catch((e) => formik.setFieldError('form', e.message))
  }, [auth, formik, ready])

  const appleSignup = React.useCallback(async () => {
    formik.setSubmitting(true)

    try {
      const url = await auth.appleSignUp(
        parseInt(formik.values.country),
        planId
      )

      window.location.assign(url)
    } catch (err) {
      formik.setFieldError(
        'form',
        "Sorry, we couldn't sign you up with Apple. Please choose another method or contact support."
      )
    } finally {
      formik.setSubmitting(false)
    }
  }, [auth, formik, planId])

  const googleSignup = React.useCallback(async () => {
    formik.setSubmitting(true)

    try {
      const url = await auth.googleSignUp(
        parseInt(formik.values.country),
        planId
      )

      window.location.assign(url)
    } catch (err) {
      formik.setFieldError(
        'form',
        "Sorry, we couldn't sign you up with Google. Please choose another method or contact support."
      )
    } finally {
      formik.setSubmitting(false)
    }
  }, [auth, formik, planId])

  return [formik, ready, googleSignup, appleSignup, toggleSignup, signupOpen]
}
